import React, { useEffect, useState, useMemo } from 'react';
import DefaultTextField from 'components/Utils/DefaultTextField';
import DefaultButton from 'components/Utils/NewDefaultButton';
import DefaultDatePicker from 'components/Utils/DefaultDatePicker';
import DefaultSelectField from 'components/Utils/DefaultSelectField';
import moment from 'moment-timezone';
import MiscUtils from 'services/MiscUtils';
import { useAppDispatch } from 'careand-redux/useAppDispatch';
import { useAppSelector } from 'careand-redux/store';
import { api } from 'careand-redux/api/ApiThunks';
import CalendarUtils from 'services/CalendarUtils';

const FORMAT = 'YYYY/MM/DD';
const NAME = 'IVF Transfer';

const TRANSFER_TYPE_LABELS = {
  3: '3 day transfer',
  5: '5 day transfer',
  fresh: 'fresh egg cycle',
};

// Export encode/decode functions for use in CPPListElement
export const IVFCalculatorUtils = {
  NAME,
  FORMAT,
  TRANSFER_TYPE_LABELS,

  /**
   * Decode an IVF Transfer note into display format for CPP
   * @param {object} item - The health record item
   * @returns {object|null} - { text, notes } or null if shouldn't display
   */
  decodeForDisplay(item) {
    let dataStr = item.history?.find(({ value }) => {
      let split = value.notes?.split('|');
      return split?.length === 2;
    })?.value?.notes;

    if (!dataStr) return null;

    const [dateStr, transferType] = dataStr.split('|');
    let transferDate = moment(dateStr, FORMAT);

    // Calculate gestational age based on transfer type
    let daysSinceTransfer = moment().diff(transferDate, 'days');
    let adjustedDays = daysSinceTransfer;

    if (transferType === '3') {
      adjustedDays += 17; // 14 days + 3 days of embryo age
    } else if (transferType === '5') {
      adjustedDays += 19; // 14 days + 5 days of embryo age
    } else if (transferType === 'fresh') {
      adjustedDays += 14; // 14 days from ovulation
    }

    let weeksVal = Math.floor(adjustedDays / 7);
    let daysLeft = Math.floor(adjustedDays % 7);

    // Don't show if more than 43 weeks
    if (weeksVal > 43) return null;

    let weeks = `${weeksVal}w`;
    if (daysLeft >= 1) weeks += ` + ${daysLeft}d`;

    // Calculate due date
    let dueDate = moment(transferDate);
    if (transferType === '3') {
      dueDate.add(263, 'days');
    } else if (transferType === '5') {
      dueDate.add(261, 'days');
    } else if (transferType === 'fresh') {
      dueDate.add(266, 'days');
    }

    let transferDateStr = CalendarUtils.format(transferDate.unix(), FORMAT);
    let dueDateStr = CalendarUtils.format(dueDate.unix(), FORMAT);

    return {
      text: `Gestational Age ${weeks} (IVF)`,
      notes: (
        <span>
          Transfer {transferDateStr} ({TRANSFER_TYPE_LABELS[transferType]})
          <br />
          EDD {dueDateStr}
        </span>
      ),
    };
  },
};

const IVFCalculatorModal = ({ patient, onSubmit }) => {
  const dispatch = useAppDispatch();

  const patientHr = useAppSelector((state) => state.healthRecords.byId[patient.id]);

  const previousIVFTransfer = useMemo(() => {
    const notes = patientHr?.healthRecords?.notes ?? [];
    let item = notes.find(
      (note) => note.value.name === NAME && note.history[0].action !== 'Removed',
    );
    return item?.history?.find(({ value }) => {
      let split = value.notes?.split('|');
      return split?.length === 2;
    })?.value?.notes;
  }, [patientHr]);

  const previousData = useMemo(() => {
    if (!previousIVFTransfer) return null;
    const [dateStr, type] = previousIVFTransfer.split('|');
    return {
      date: moment(dateStr, FORMAT).unix(),
      type: type,
    };
  }, [previousIVFTransfer]);

  const [transferDate, setTransferDate] = useState(previousData?.date || moment().unix());
  const [transferType, setTransferType] = useState(previousData?.type || '5');
  const [dueDate, setDueDate] = useState(null);
  const [weeks, setWeeks] = useState('');
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    if (transferDate) {
      calculateIVFDueDate();
    }
  }, [transferDate, transferType]);

  function calculateIVFDueDate() {
    const transfer = moment.unix(transferDate);
    let calculatedDueDate = moment(transfer);

    // Calculate due date based on transfer type
    if (transferType === '3') {
      // For 3-day transfer: 280 - 14 - 3 = 263 days from transfer
      calculatedDueDate.add(263, 'days');
    } else if (transferType === '5') {
      // For 5-day transfer: 280 - 14 - 5 = 261 days from transfer
      calculatedDueDate.add(261, 'days');
    } else if (transferType === 'fresh') {
      // For fresh cycle: 280 - 14 = 266 days from egg retrieval
      calculatedDueDate.add(266, 'days');
    }

    setDueDate(calculatedDueDate.unix());

    // Calculate current gestational age
    const gestAge = calculateGestationalAge(transfer, moment(), transferType);
    setWeeks(gestAge);
  }

  function calculateGestationalAge(transferDate, currentDate, transferType) {
    let difference = currentDate.diff(transferDate, 'days');

    // Add days based on transfer type
    if (transferType === '3') {
      difference += 17; // 14 days + 3 days of embryo age
    } else if (transferType === '5') {
      difference += 19; // 14 days + 5 days of embryo age
    } else if (transferType === 'fresh') {
      difference += 14; // 14 days from ovulation
    }

    const weeksVal = Math.floor(difference / 7);
    const daysLeft = Math.floor(difference % 7);
    let result = `${weeksVal}w`;
    if (daysLeft >= 1) result += ` + ${daysLeft}d`;
    return result;
  }

  function onChangeDueDate(date) {
    if (date) {
      setDueDate(date);
      // Calculate transfer date based on due date and transfer type
      let momentDate = moment.unix(date);
      let transfer;
      if (transferType === '3') {
        transfer = momentDate.add(-263, 'days');
      } else if (transferType === '5') {
        transfer = momentDate.add(-261, 'days');
      } else if (transferType === 'fresh') {
        transfer = momentDate.add(-266, 'days');
      }
      setTransferDate(transfer.unix());
    }
  }

  async function save() {
    let data = {
      value: {
        name: NAME,
        notes: `${moment.unix(transferDate).format(FORMAT)}|${transferType}`,
      },
    };

    if (previousIVFTransfer) data.id = previousIVFTransfer.id;

    setSaving(true);
    await dispatch(
      api.HealthRecords.addOrEditHealthRecordPatientIssue({
        patientId: patient.id,
        field: 'notes',
        patientIssues: [data],
      }),
      {
        errorTitle: 'IVF Transfer',
      },
    );
    setSaving(false);
  }

  function getResult() {
    let result = '';
    if (dueDate) result += `Due date: ${moment.unix(dueDate).format(FORMAT)}\n`;
    if (weeks) result += `Weeks of pregnancy: ${weeks}\n`;
    return result;
  }

  async function copyToClipboard() {
    const result = getResult();

    if (result) {
      await navigator.clipboard.writeText(result);
      MiscUtils.showToastrInfo({
        title: 'IVF Calculator',
        message: 'Values copied successfully!',
      });
    }
  }

  return (
    <div className="modal" style={{ width: '300px', height: '100%' }}>
      <div className="title">IVF Calculator</div>
      <div className="padded-box">
        <div className="row">
          <DefaultDatePicker
            label="Transfer Date"
            autoFocus
            name="transfer_date"
            value={transferDate}
            onChange={(event, date) => setTransferDate(date)}
            style={{ width: '100%' }}
          />
          <DefaultSelectField
            label="Transfer Type"
            value={transferType}
            onChange={(event) => setTransferType(event.target.value)}
            inputs={Object.keys(TRANSFER_TYPE_LABELS)}
            inputElements={Object.keys(TRANSFER_TYPE_LABELS).map((key) => (
              <option key={key} value={key}>
                {TRANSFER_TYPE_LABELS[key]}
              </option>
            ))}
            style={{ width: '100%', marginTop: '10px' }}
          />
          <div className="row">
            <DefaultTextField
              name="weeks"
              label="Weeks Pregnant"
              value={weeks || ''}
              disabled={true}
              style={{ width: 'calc(50% - 10px)', marginRight: '20px' }}
            />
            <DefaultDatePicker
              key={dueDate}
              label="Due Date (EDD)"
              name="due_date"
              value={dueDate}
              onChange={(event, date) => onChangeDueDate(date)}
              style={{ width: 'calc(50% - 10px)' }}
            />
          </div>
        </div>
        <DefaultButton
          name={onSubmit ? 'Submit' : 'Copy to Clipboard'}
          onClick={
            onSubmit
              ? () => {
                  onSubmit(getResult());
                }
              : copyToClipboard
          }
          style={{ width: '100%', marginTop: '30px' }}
          disabled={!weeks && !dueDate}
        />
        <DefaultButton
          name="Save IVF Transfer Date"
          className="blue"
          onClick={save}
          style={{ width: '100%', marginTop: '10px' }}
          disabled={!weeks && !dueDate}
          loading={saving}
        />
      </div>
    </div>
  );
};

export default IVFCalculatorModal;
